import { useState } from "react";
import { Link } from "react-router-dom";
import { PenLine, Film, Clapperboard, Headphones, ShoppingBag, GraduationCap, Settings, Mail, Loader2, LogIn, ArrowLeft, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ContentManager from "./admin/ContentManager";
import ProductManager from "./admin/ProductManager";
import CourseManager from "./admin/CourseManager";
import SettingsManager from "./admin/SettingsManager";
import MessagesManager from "./admin/MessagesManager";
import ProgramManager from "./admin/ProgramManager";

const TABS = [
  { id: "program", label: "Prodotto · Percorso", icon: Sparkles },
  { id: "writing", label: "Scrittura", icon: PenLine },
  { id: "video", label: "Video", icon: Film },
  { id: "short", label: "Short Video", icon: Clapperboard },
  { id: "audio", label: "Audio / Podcast", icon: Headphones },
  { id: "shop", label: "Shop", icon: ShoppingBag },
  { id: "corsi", label: "Corsi", icon: GraduationCap },
  { id: "settings", label: "Impostazioni", icon: Settings },
  { id: "messages", label: "Messaggi", icon: Mail },
];

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("program");

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C6899E]" /></div>;

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="admin-login-required">
        <h1 className="font-serif text-3xl mb-4">Area riservata</h1>
        <p className="text-[#8C7E83] mb-8">Accedi per gestire i tuoi contenuti.</p>
        <button onClick={login} data-testid="admin-login-btn"
          className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C]">
          <LogIn className="w-4 h-4" /> Accedi con Google
        </button>
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="admin-denied">
        <h1 className="font-serif text-3xl mb-4">Accesso negato</h1>
        <p className="text-[#8C7E83] mb-8">Questa area è riservata all'amministratore.</p>
        <Link to="/" className="link-underline text-sm uppercase tracking-[0.15em]">Torna alla home</Link>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="eyebrow mb-2">CMS</div>
          <h1 className="font-serif text-4xl font-medium tracking-tight">Gestione UNFILTERED</h1>
        </div>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#9C8F94] hover:text-[#C6899E]"><ArrowLeft className="w-4 h-4" /> Sito</Link>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
            {TABS.map((tb) => {
              const Icon = tb.icon;
              return (
                <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`admin-tab-${tb.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm whitespace-nowrap transition-colors ${tab === tb.id ? "bg-[#FFFFFF] text-[#C6899E] border border-[rgba(198,137,158,0.3)]" : "text-[#8C7E83] hover:text-[#3D3238] border border-transparent"}`}>
                  <Icon className="w-4 h-4" /> {tb.label}
                </button>
              );
            })}
          </div>
        </aside>

        <section className="bg-[#FFFFFF] rounded-2xl border border-[rgba(61,50,56,0.08)] p-6 sm:p-8 min-h-[500px]">
          {["writing", "video", "short", "audio"].includes(tab) && <ContentManager key={tab} type={tab} />}
          {tab === "program" && <ProgramManager />}
          {tab === "shop" && <ProductManager />}
          {tab === "corsi" && <CourseManager />}
          {tab === "settings" && <SettingsManager />}
          {tab === "messages" && <MessagesManager />}
        </section>
      </div>
    </div>
  );
}
